/**
 * CommentThread component
 *
 * Renders a list of comments for a security issue with user info,
 * timestamps, comment type badges, content, and edit/delete controls
 * for the current user's own comments.
 */

import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare, Pencil, Trash2, X, Check } from "lucide-react";
import type { IssueComment } from "@/types/issueComment";
import { useUpdateIssueComment, useDeleteIssueComment } from "@/hooks/useIssueComments";
import { COMMENT_TYPE_LABELS, COMMENT_TYPE_COLORS } from "@/constants/severity";

function formatTimestamp(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

interface CommentItemProps {
  comment: IssueComment;
  issueId: number;
  isOwnComment: boolean;
  isOrgOwner: boolean;
}

function CommentItem({ comment, issueId, isOwnComment, isOrgOwner }: CommentItemProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);

  const updateMutation = useUpdateIssueComment();
  const deleteMutation = useDeleteIssueComment();

  const handleSaveEdit = () => {
    if (!editContent.trim()) return;
    updateMutation.mutate(
      { issueId, commentId: comment.id, data: { content: editContent.trim() } },
      {
        onSuccess: () => {
          setIsEditing(false);
        },
      }
    );
  };

  const handleCancelEdit = () => {
    setEditContent(comment.content);
    setIsEditing(false);
  };

  const handleDelete = () => {
    deleteMutation.mutate({ issueId, commentId: comment.id });
  };

  const canDelete = isOwnComment || isOrgOwner;

  return (
    <div className="flex gap-3 py-4 border-b last:border-b-0">
      {/* Avatar placeholder */}
      <div className="flex-shrink-0 w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
        <span className="text-xs font-medium text-gray-600">
          {comment.user_name ? comment.user_name.charAt(0).toUpperCase() : "?"}
        </span>
      </div>

      <div className="flex-1 min-w-0">
        {/* Header row */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium">
            {comment.user_name || "Unknown User"}
          </span>
          <span className="text-xs text-muted-foreground">
            {formatTimestamp(comment.created_at)}
          </span>
          {comment.is_edited && (
            <span className="text-xs text-muted-foreground italic">(edited)</span>
          )}
          {comment.comment_type !== "GENERAL" && (
            <Badge
              variant="outline"
              className={`text-[10px] px-1.5 py-0 ${COMMENT_TYPE_COLORS[comment.comment_type]}`}
            >
              {COMMENT_TYPE_LABELS[comment.comment_type]}
            </Badge>
          )}
        </div>

        {/* Content or edit form */}
        {isEditing ? (
          <div className="mt-2 space-y-2">
            <Textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="min-h-[60px] text-sm"
              maxLength={10000}
            />
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="default"
                className="h-7 gap-1 text-xs"
                onClick={handleSaveEdit}
                disabled={
                  updateMutation.isPending ||
                  !editContent.trim() ||
                  editContent.trim() === comment.content
                }
              >
                <Check className="h-3 w-3" />
                Save
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 gap-1 text-xs"
                onClick={handleCancelEdit}
                disabled={updateMutation.isPending}
              >
                <X className="h-3 w-3" />
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <p className="mt-1 text-sm whitespace-pre-wrap break-words">{comment.content}</p>
        )}

        {/* Actions */}
        {!isEditing && (isOwnComment || canDelete) && (
          <div className="mt-2 flex items-center gap-1">
            {isOwnComment && (
              <Button
                size="sm"
                variant="ghost"
                className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                onClick={() => {
                  setEditContent(comment.content);
                  setIsEditing(true);
                }}
              >
                <Pencil className="h-3 w-3 mr-1" />
                Edit
              </Button>
            )}
            {canDelete && (
              <Button
                size="sm"
                variant="ghost"
                className="h-6 px-2 text-xs text-muted-foreground hover:text-destructive"
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
              >
                <Trash2 className="h-3 w-3 mr-1" />
                Delete
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

interface CommentThreadProps {
  comments: IssueComment[];
  issueId: number;
  currentUserId: number | undefined;
  isOrgOwner: boolean;
}

export function CommentThread({
  comments,
  issueId,
  currentUserId,
  isOrgOwner,
}: CommentThreadProps) {
  if (comments.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-20" />
        <p className="text-sm">No comments yet</p>
        <p className="text-xs mt-1">Be the first to add a comment on this issue</p>
      </div>
    );
  }

  return (
    <div className="divide-y-0">
      {comments.map((comment) => (
        <CommentItem
          key={comment.id}
          comment={comment}
          issueId={issueId}
          isOwnComment={currentUserId != null && comment.user_id === currentUserId}
          isOrgOwner={isOrgOwner}
        />
      ))}
    </div>
  );
}
